import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Rating from '@mui/material/Rating';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import actions from '../../../../redux/actions';

export interface ICustomerReview {
    testerId: number;
    rating: number;
    text: string;
    createdAt?: string;
}

interface ITesterReviewDialogProps {
    open: boolean;
    cycleId: number;
    testerId: number;
    testerName: string;
    review?: ICustomerReview;
    onClose: () => void;
    onSubmitted?: () => void;
}

/* 1-5 stars + comment. wt requires both (CreateCustomerReviewHttpDto) and marks the tester
   Finished on success, which is what flips the card to "Reviewed".
   With `review` it only shows that existing review. */
const TesterReviewDialog: React.FC<ITesterReviewDialogProps> = ({ open, cycleId, testerId, testerName, review, onClose, onSubmitted }) => {
    const dispatch = useDispatch();

    const [rating, setRating] = useState<number | null>(null);
    const [text, setText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setRating(null);
            setText('');
            setError('');
        }
    }, [open]);

    const canSubmit = !!rating && text.trim().length > 0 && !submitting;

    const onSubmit = async () => {
        setSubmitting(true);
        const result: any = await dispatch(actions.createCustomerReview(cycleId, testerId, rating, text.trim()));
        setSubmitting(false);

        if (result?.type === 'SUBMIT_CUSTOMER_REVIEW_FAIL') {
            setError('The review could not be saved. Please try again.');
            return;
        }
        onSubmitted?.();
        onClose();
    };

    if (review) {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Your review of {testerName}</DialogTitle>
                <DialogContent>
                    <Box display="flex" alignItems="center" mb={2} mt={1}>
                        <Rating name="tester-rating-readonly" value={review.rating} readOnly size="large" />
                        {review.createdAt && (
                            <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                                {moment(review.createdAt).format('DD/MM/YYYY')}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{review.text}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Review {testerName}</DialogTitle>
            <DialogContent>
                <Box display="flex" alignItems="center" mb={2} mt={1}>
                    <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>Rating</Typography>
                    <Rating
                        name="tester-rating"
                        value={rating}
                        onChange={(_, value) => setRating(value)}
                        size="large"
                    />
                </Box>
                <TextField
                    label="Comment"
                    placeholder="How did this tester do? The tester will see this review."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    inputProps={{ maxLength: 5000 }}
                    multiline
                    minRows={4}
                    fullWidth
                    required
                />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button variant="contained" onClick={onSubmit} disabled={!canSubmit}>
                    Submit review
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TesterReviewDialog;
